import { Injectable, Output } from '@angular/core';
import { DocsService, ICreateMappingBookRequest, IDeleteMappingBookRequest, IImportedMappingBookRequest, IMappingBook } from '@services/docs.service';
import { combineAllWith } from '@shared/combineAllWith';
import { readFiles } from '@shared/fileReaderUtils';

import { UploadStatus, ValidatorService } from '@shared/models/fileupload';
import { ISelectableData } from '@shared/models/generictablecolumn';
import { catchError, from, map, mergeMap, Observable, of, Subject, take } from 'rxjs';

import * as XLSX from 'xlsx';

@Injectable({
	providedIn: 'root',
})
export class MappingValidatorService implements ValidatorService {
	private statusSubject: Subject<UploadStatus> = new Subject<UploadStatus>();
	public statusSubject$ = this.statusSubject.asObservable();

	@Output() public allowedFileTypes: string[] = ['.xlsx'];

	constructor(private docsService: DocsService) {}

	validate(files: File[], _: any, context: Observable<ISelectableData<IMappingBook>[]>) {
		this.importFileAndCreateMappingBooks(files[0], context);
	}

	importFileAndCreateMappingBooks(file: File, context: Observable<ISelectableData<IMappingBook>[]>): void {
		readFiles([file], 'binary')
			.pipe(
				mergeMap((text) => {
					try {
						const templates = this.extractData(text);
						return from(templates);
					} catch (error) {
						console.error(error);
						throw new Error('Failed to parse XLSX');
					}
				}),
				combineAllWith(context.pipe(take(1))),
				mergeMap(([mapping, ExistingMappings]) => {
					const match = ExistingMappings.find((existingMapping) => existingMapping.tags === mapping.tags);

					if (match) {
						console.log('Found existing mapping. Deleting..', match);
						const request = { type: 'book' } as IDeleteMappingBookRequest;
						return this.docsService.deleteMappingBook(match.self, request).pipe(
							catchError((error) => {
								console.error(error);
								throw new Error('Failed to delete mappingBooks Api Error');
							}),
							map(() => mapping),
						);
					}

					return of(mapping);
				}),
				mergeMap((importedMapping) => {
					const request: ICreateMappingBookRequest = {
						mappingDocument: importedMapping.mappingDocument,
						tags: this.extractTags(importedMapping.tags),
					};

					return this.docsService.createMappingBook(request).pipe(
						catchError((error) => {
							console.error(error);
							throw new Error('Failed to create mappingBooks Api Error');
						}),
					);
				}),
			)
			.subscribe({
				complete: () => {
					this.statusSubject.next({ status: 'success', data: 'Import complete' });
				},
				error: (error: Error) => {
					this.statusSubject.next({ status: 'error', data: error.message });
				},
			});
	}

	extractData(file: ArrayBuffer): IImportedMappingBookRequest[] {
		if (file.byteLength === 0) {
			throw new Error('File is empty');
		}

		const sheet = XLSX.read(file, { type: 'binary' }).Sheets['Mappings'];

		const rows: Array<[tags: string, key: string, value: string]> = XLSX.utils.sheet_to_json(sheet, { header: 1 });

		console.log(rows);

		if (rows.length === 1) {
			throw new Error('File contains only header');
		}

		// remove header
		const header = rows.shift();

		if (header[0] !== 'tags' || header[1] !== 'key' || header[2] !== 'value') {
			throw new Error('Invalid header');
		}

		const mappings = rows.reduce(
			(acc, row) => {
				// skip empty rows
				const [tags, key, value] = row;

				if (acc[tags]) {
					const map = acc[tags];

					if (map[key]) {
						throw new Error(`Mapping ${tags} contains duplicate of key: ${key}`);
					}

					map[key] = value;
				} else {
					acc[tags] = { [key]: value };
				}

				return acc;
			},
			{} as Record<string, Record<string, string>>,
		);

		return Object.entries(mappings).map(([tags, mappingDocument]) => {
			return {
				mappingDocument: {
					'--systemspecificmappings--': mappingDocument,
				},
				tags: tags.replace(/;/g, ', '),
			};
		});
	}

	extractTags(tags: string): Record<string, string> {
		return Object.fromEntries(tags.split(', ').map((tag) => tag.trim().split('=')));
	}

	// private saveFile(files, budgets): Observable<any> {
	// 	const createBudget: IInsightsCreateBudget = {
	// 		data: JSON.stringify(JSON.stringify(budgets)),
	// 		name: 'budget',
	// 	};
	// 	return this.insightsService.addBudget(createBudget).pipe(take(1));
	// }
}
